import type { BotCheckAction } from "@station-keeping/contracts";

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TIMEOUT_MS = 60_000;

interface TurnstileOptions {
  sitekey: string;
  action: string;
  appearance: "always" | "execute" | "interaction-only";
  theme: "light" | "dark" | "auto";
  language: string;
  callback(token: string): void;
  "error-callback"(): void;
  "expired-callback"(): void;
}

interface Turnstile {
  render(container: HTMLElement, options: TurnstileOptions): string | undefined;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

let loading: Promise<Turnstile> | null = null;

function loadTurnstile(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<Turnstile>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile absent")));
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error("Turnstile injoignable"));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/**
 * Jeton Cloudflare Turnstile pour une action (la demande de code), sur le web. Le widget ne
 * se montre que si Cloudflare demande une interaction. Dans l'app, il passera par une page du site en iframe
 * (lot Capacitor) : le domaine d'une webview n'est pas accepté par Turnstile.
 */
export async function botCheckToken(siteKey: string, action: BotCheckAction): Promise<string> {
  const turnstile = await loadTurnstile();
  const host = document.createElement("div");
  host.className = "fixed bottom-24 left-1/2 z-50 -translate-x-1/2";
  document.body.appendChild(host);
  let widgetId: string | undefined;
  try {
    return await new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Turnstile : délai dépassé")), TIMEOUT_MS);
      const done = (fn: () => void) => {
        clearTimeout(timer);
        fn();
      };
      widgetId = turnstile.render(host, {
        sitekey: siteKey,
        action,
        appearance: "interaction-only",
        theme: "dark",
        language: "fr",
        callback: (token) => done(() => resolve(token)),
        "error-callback": () => done(() => reject(new Error("Turnstile : échec"))),
        "expired-callback": () => done(() => reject(new Error("Turnstile : jeton expiré"))),
      });
    });
  } finally {
    if (widgetId) turnstile.remove(widgetId);
    host.remove();
  }
}
